import React, { useState, useEffect } from "react";
import { X, Loader2, HardDrive } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

const TRIGGER_CLS = "w-full bg-white/5 border-white/10 text-white hover:bg-white/10 focus:ring-emerald-500 data-[placeholder]:text-white/40";
const CONTENT_CLS = "z-[110] bg-[#0F0F17] border-white/10 text-white shadow-2xl";
const ITEM_CLS = "text-white focus:bg-white/10 focus:text-white data-[state=checked]:bg-emerald-500/20 data-[state=checked]:text-emerald-300";

export default function ProvisioningFormDialog({ tenants = [], open, onClose, onCreated }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ tenant_id: tenants[0]?.tenant_id || "", notes: "" });
  }, [open, tenants]);

  if (!open || !form) return null;
  const set = (patch) => setForm({ ...form, ...patch });

  const submit = async () => {
    if (!form.tenant_id) { toast.error("Pilih tenant dulu."); return; }
    setBusy(true);
    try {
      const payload = { tenant_id: form.tenant_id };
      if (form.notes) payload.notes = form.notes;
      const r = await api.post("/platform/provisioning", payload);
      toast.success(`Provision ${r.data.provision.provision_number} dibuat.`);
      onCreated?.(r.data.provision);
      onClose?.();
    } catch (e) { notifyError(e, "Gagal membuat provision"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm grid place-items-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()} data-testid="provision-form">
      <div className="w-full max-w-lg bg-[#0F0F17] border border-white/10 rounded-3xl">
        <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
          <div className="text-white font-display font-black text-lg">Buat Provisioning</div>
          <button onClick={onClose} className="ml-auto w-8 h-8 rounded-lg grid place-items-center text-white/60 hover:text-white hover:bg-white/10">
            <X size={16} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Tenant *</label>
            <Select value={form.tenant_id} onValueChange={(v) => set({ tenant_id: v })}>
              <SelectTrigger className={TRIGGER_CLS} data-testid="prov-form-tenant"><SelectValue placeholder="Pilih tenant…" /></SelectTrigger>
              <SelectContent className={CONTENT_CLS}>
                {tenants.map((t) => (
                  <SelectItem key={t.tenant_id} value={t.tenant_id} className={ITEM_CLS}>{t.tenant_name} · {t.tenant_code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Notes (opsional)</label>
            <Input value={form.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="Catatan provision…" className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="prov-form-notes" />
          </div>
          <div className="text-[10px] text-white/40 leading-relaxed">
            Provisioning ini foundation only — akan menghasilkan <strong className="text-white/70">blueprint database, storage, migration plan, seed plan, dan admin account plan</strong>. TIDAK membuat resource sungguhan.
          </div>
        </div>
        <div className="px-6 py-4 border-t border-white/10 flex items-center gap-2 justify-end">
          <Button variant="ghost" onClick={onClose} className="text-white/60 hover:text-white hover:bg-white/10">Batal</Button>
          <Button onClick={submit} disabled={busy || !form.tenant_id} className="bg-emerald-500 text-white hover:bg-emerald-600" data-testid="prov-form-submit">
            {busy ? <><Loader2 size={14} className="mr-1 animate-spin" /> Membuat…</> : <><HardDrive size={14} className="mr-1" /> Buat</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
